(function () {
  const App = window.WallArtApp;
  const VIEWBOX_WIDTH = 1000;
  const VIEWBOX_HEIGHT = 620;
  const SIDE_MARGIN = 34;
  const TOP_MARGIN = 44;
  const BOTTOM_MARGIN = 36;
  const FRAME_THICKNESS_CM = 1.8;
  const WALL_STROKE = 0.9;
  const FURNITURE_STROKE = 1.15;
  const ART_STROKE = 2.5;
  const MEASURE_STROKE = 0.75;
  const TICK_SIZE = 8;
  const COLOURS = {
    background: "#F8F6F1",
    wallFill: "#FCFBF8",
    wallStroke: "#D8D0C4",
    usableGuide: "#D8D0C4",
    artwork: "#2B2B2B",
    artworkInner: "#F2EEE6",
    furnitureFill: "#FBFAF7",
    furnitureStroke: "#A89F92",
    centreLine: "#D8D0C4",
    measure: "#A8A098",
    measureText: "#5A534A",
    measureUnit: "#8A8278",
    title: "#7A7268"
  };
  const FRAME_COLOURS = {
    outer: COLOURS.artwork,
    mount: "#F7F7F7",
    paper: "#FBFAF7",
    innerLine: "#A8A098",
    stroke: COLOURS.artwork
  };
  const FURNITURE_OUTLINE = "#111";
  const FURNITURE_STROKE_STYLE = "stroke:" + FURNITURE_OUTLINE + ";stroke-width:" + FURNITURE_STROKE.toFixed(2) + "px;vector-effect:non-scaling-stroke;";
  const FURNITURE_FILL_STYLE = "fill:" + COLOURS.furnitureFill + ";";
  const FURNITURE_LINE_STYLE = "fill:none;" + FURNITURE_STROKE_STYLE;

  function esc(text) {
    return String(text)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function layoutLabel(layout) {
    const labels = {
      single: "Single piece",
      pair: "Pair",
      triptych: "Triptych"
    };

    return labels[layout] || layout.replace(/_/g, " ");
  }

  function orientationLabel(orientation) {
    return orientation.charAt(0).toUpperCase() + orientation.slice(1);
  }

  function roomLabel(roomType) {
    const labels = {
      living: "Living Room",
      bedroom: "Bedroom",
      dining: "Dining Room",
      bathroom: "Bathroom",
      kitchen: "Kitchen",
      hallway: "Hallway"
    };

    if (!roomType) {
      return "";
    }

    return labels[roomType] || String(roomType)
      .replace(/_/g, " ")
      .replace(/\b\w/g, function (letter) { return letter.toUpperCase(); });
  }

  function accessibleMeasurement(value) {
    if (value == null || Number.isNaN(value)) {
      return "unknown";
    }

    return App.formatValue(value) + (App.state.region === "imperial" ? " inches" : " centimetres");
  }

  function previewTitle(engineResult) {
    return "Room preview: " + roomLabel(engineResult.resolvedInputs && engineResult.resolvedInputs.roomType);
  }

  function previewDescription(engineResult, geometry) {
    const recommendation = engineResult.recommendation;
    const artwork = geometry.artwork;
    const pieceLabel = artwork.pieceCount === 1 ? "print" : "prints";
    const parts = [
      "Shows " + artwork.pieceCount + " " + recommendation.isoSize + " " + recommendation.orientation + " " + pieceLabel +
        " in a " + layoutLabel(recommendation.layout).toLowerCase() + " layout",
      accessibleMeasurement(artwork.totalArtworkWidthCm) + " total width",
      "centred at " + accessibleMeasurement(artwork.centreFromFloorCm) + " from the floor"
    ];

    if (
      geometry.anchor &&
      geometry.anchor.type !== "none" &&
      artwork.clearanceAboveFurnitureCm != null
    ) {
      parts.push(accessibleMeasurement(artwork.clearanceAboveFurnitureCm) + " above the furniture");
    }

    return parts.join(", ") + ".";
  }

  function setPreviewHeading(engineResult) {
    const label = App.dom.previewLabel || document.getElementById("previewLabel");

    if (!label) {
      return;
    }

    const roomName = engineResult && engineResult.resolvedInputs
      ? roomLabel(engineResult.resolvedInputs.roomType)
      : "";
    label.textContent = roomName ? "Room preview: " + roomName : "Room preview";
  }

  function formatMeasurement(value, unit) {
    return {
      number: App.formatValue(value),
      unit: unit || App.unitLabel()
    };
  }

  function measurementLabel(x, y, label, value, unit, anchor) {
    const formatted = formatMeasurement(value, unit);
    return [
      "<text x='" + x.toFixed(1) + "' y='" + y.toFixed(1) + "' text-anchor='" + (anchor || "middle") + "' font-family='Jost, sans-serif'>",
      "<tspan fill='" + COLOURS.measureText + "' font-size='14' font-weight='500'>" + esc(String(formatted.number)) + "</tspan>",
      "<tspan dx='4' fill='" + COLOURS.measureUnit + "' font-size='11' font-weight='400'>" + esc(formatted.unit) + "</tspan>",
      label ? "<tspan dx='6' fill='" + COLOURS.title + "' font-size='10.5' letter-spacing='0.6'>" + esc(label) + "</tspan>" : "",
      "</text>"
    ].join("");
  }

  function widthDimensionLine(x1, x2, y, value) {
    return [
      "<g>",
      "<line x1='" + x1.toFixed(1) + "' y1='" + y.toFixed(1) + "' x2='" + x2.toFixed(1) + "' y2='" + y.toFixed(1) + "' stroke='" + COLOURS.measure + "' stroke-width='" + MEASURE_STROKE + "'/>",
      "<line x1='" + x1.toFixed(1) + "' y1='" + (y - TICK_SIZE).toFixed(1) + "' x2='" + x1.toFixed(1) + "' y2='" + (y + TICK_SIZE).toFixed(1) + "' stroke='" + COLOURS.measure + "' stroke-width='" + MEASURE_STROKE + "' stroke-linecap='round'/>",
      "<line x1='" + x2.toFixed(1) + "' y1='" + (y - TICK_SIZE).toFixed(1) + "' x2='" + x2.toFixed(1) + "' y2='" + (y + TICK_SIZE).toFixed(1) + "' stroke='" + COLOURS.measure + "' stroke-width='" + MEASURE_STROKE + "' stroke-linecap='round'/>",
      measurementLabel((x1 + x2) / 2, y - 12, "", value),
      "</g>"
    ].join("");
  }

  function gapDimensionLine(x, y1, y2, value) {
    const guideGap = 5;
    const topY = Math.min(y1, y2);
    const bottomY = Math.max(y1, y2);
    const lineTopY = topY + guideGap;
    const lineBottomY = bottomY - guideGap;

    return [
      "<g>",
      "<line x1='" + x.toFixed(1) + "' y1='" + lineTopY.toFixed(1) + "' x2='" + x.toFixed(1) + "' y2='" + lineBottomY.toFixed(1) + "' stroke='" + COLOURS.measure + "' stroke-width='" + MEASURE_STROKE + "'/>",
      "<line x1='" + (x - TICK_SIZE).toFixed(1) + "' y1='" + lineTopY.toFixed(1) + "' x2='" + (x + TICK_SIZE).toFixed(1) + "' y2='" + lineTopY.toFixed(1) + "' stroke='" + COLOURS.measure + "' stroke-width='" + MEASURE_STROKE + "' stroke-linecap='round'/>",
      "<line x1='" + (x - TICK_SIZE).toFixed(1) + "' y1='" + lineBottomY.toFixed(1) + "' x2='" + (x + TICK_SIZE).toFixed(1) + "' y2='" + lineBottomY.toFixed(1) + "' stroke='" + COLOURS.measure + "' stroke-width='" + MEASURE_STROKE + "' stroke-linecap='round'/>",
      measurementLabel(x + 14, (y1 + y2) / 2 + 4, "", value, App.unitLabel(), "start"),
      "</g>"
    ].join("");
  }

  function centreInstructionLabel(x, y, value, options) {
    const compact = options && options.compact;
    return [
      "<text x='" + x.toFixed(1) + "' y='" + y.toFixed(1) + "' text-anchor='middle' font-family='Jost, sans-serif'>",
      "<tspan fill='" + COLOURS.measureText + "' font-size='14' font-weight='500'>Centre:</tspan>",
      "<tspan dx='6' fill='" + COLOURS.measureText + "' font-size='14' font-weight='500'>" + esc(String(App.formatValue(value))) + "</tspan>",
      "<tspan dx='4' fill='" + COLOURS.measureUnit + "' font-size='11' font-weight='400'>" + esc(App.unitLabel()) + "</tspan>",
      compact ? "" : "<tspan dx='5' fill='" + COLOURS.title + "' font-size='10.5' font-weight='400'>from floor</tspan>",
      "</text>"
    ].join("");
  }

  function getSofaSeatCount(engineResult, anchorWidthCm) {
    const furnitureTypeKey = engineResult &&
      engineResult.resolvedInputs &&
      engineResult.resolvedInputs.furnitureTypeKey;

    if (furnitureTypeKey === "armchair") {
      return 1;
    }

    if (furnitureTypeKey === "sofa_2_seater") {
      return 2;
    }

    if (anchorWidthCm < 120) {
      return 1;
    }

    if (anchorWidthCm < 185) {
      return 2;
    }

    return 3;
  }

  function sofaMarkup(x, y, width, height, seatCount) {
    const sofaFill = COLOURS.furnitureFill;
    const mainStroke = FURNITURE_STROKE;
    const source = {
      top: 352,
      height: 304.5,
      leftArmLeft: 157,
      leftArmWidth: 50,
      rightArmLeft: 500,
      cushionLeft: 207,
      cushionWidth: 293,
      seatBaseTop: 568,
      seatBaseHeight: 28,
      legBarLeft: 192.5,
      legBarTop: 596,
      legBarHeight: 18,
      leftLegLeft: 171.5,
      rightLegLeft: 514.5,
      legWidth: 21,
      legHeight: 60.5
    };
    const scale = height / source.height;
    const fixedArmWidth = source.leftArmWidth * scale;
    const contentX = x + fixedArmWidth;
    const contentWidth = Math.max(8, width - (fixedArmWidth * 2));
    const moduleWidth = contentWidth / seatCount;
    const style = "fill:" + sofaFill + ";stroke:" + FURNITURE_OUTLINE + ";stroke-width:" + mainStroke.toFixed(2) + "px;vector-effect:non-scaling-stroke;";

    function yFromSource(value) {
      return y + ((value - source.top) * scale);
    }

    function transformFromSource(sourceLeft, targetLeft, scaleX, scaleY) {
      return [
        "translate(" + (targetLeft - (sourceLeft * scaleX)).toFixed(3) + " " + (y - (source.top * scaleY)).toFixed(3) + ")",
        "scale(" + scaleX.toFixed(5) + " " + scaleY.toFixed(5) + ")"
      ].join(" ");
    }

    function roundedRectPath(boxX, boxY, boxWidth, boxHeight, rx) {
      const right = boxX + boxWidth;
      const bottom = boxY + boxHeight;
      const r = Math.min(rx, boxWidth / 2, boxHeight / 2);

      return [
        "M", (boxX + r).toFixed(1), boxY.toFixed(1),
        "L", (right - r).toFixed(1), boxY.toFixed(1),
        "Q", right.toFixed(1), boxY.toFixed(1), right.toFixed(1), (boxY + r).toFixed(1),
        "L", right.toFixed(1), (bottom - r).toFixed(1),
        "Q", right.toFixed(1), bottom.toFixed(1), (right - r).toFixed(1), bottom.toFixed(1),
        "L", (boxX + r).toFixed(1), bottom.toFixed(1),
        "Q", boxX.toFixed(1), bottom.toFixed(1), boxX.toFixed(1), (bottom - r).toFixed(1),
        "L", boxX.toFixed(1), (boxY + r).toFixed(1),
        "Q", boxX.toFixed(1), boxY.toFixed(1), (boxX + r).toFixed(1), boxY.toFixed(1),
        "Z"
      ].join(" ");
    }

    function cushionModule(index) {
      const targetLeft = contentX + (moduleWidth * index);
      const moduleScaleX = moduleWidth / source.cushionWidth;
      const transform = transformFromSource(source.cushionLeft, targetLeft, moduleScaleX, scale);

      return [
        "<g transform='" + transform + "'>",
        "<g transform='matrix(1,0,0,1.028571,0,-16.228571)'>",
        "<path d='M500,390.186L500,535.814C500,553.578 485.166,568 466.894,568L240.106,568C221.834,568 207,553.578 207,535.814L207,390.186C207,372.422 221.834,358 240.106,358L466.894,358C485.166,358 500,372.422 500,390.186Z' style='" + style + "'/>",
        "</g>",
        "<g transform='matrix(1,0,0,0.404762,0,338.095238)'>",
        "<path d='M500,442.457L500,535.814C500,553.578 494.162,568 486.972,568L220.028,568C212.838,568 207,553.578 207,535.814L207,455.37C207,401.63 224.66,358 246.412,358L465.815,358C484.682,358 500,395.844 500,442.457Z' style='" + style + "'/>",
        "</g>",
        "</g>"
      ].join("");
    }

    function leftArm() {
      return [
        "<g transform='" + transformFromSource(source.leftArmLeft, x, scale, scale) + "'>",
        "<g transform='matrix(1.351351,0,0,1.141104,-55.162162,-84.09816)'>",
        "<path d='M194,443.954L194,585.046C194,591.092 189.855,596 184.75,596L166.25,596C161.145,596 157,591.092 157,585.046L157,443.954C157,437.908 161.145,433 166.25,433L184.75,433C189.855,433 194,437.908 194,443.954Z' style='" + style + "'/>",
        "</g>",
        "</g>"
      ].join("");
    }

    function rightArm() {
      return [
        "<g transform='" + transformFromSource(source.rightArmLeft, x + width - fixedArmWidth, scale, scale) + "'>",
        "<g transform='matrix(1.351351,0,0,1.141104,287.837838,-84.09816)'>",
        "<path d='M194,443.954L194,585.046C194,591.092 189.855,596 184.75,596L166.25,596C161.145,596 157,591.092 157,585.046L157,443.954C157,437.908 161.145,433 166.25,433L184.75,433C189.855,433 194,437.908 194,443.954Z' style='" + style + "'/>",
        "</g>",
        "</g>"
      ].join("");
    }

    function leftLeg() {
      return [
        "<g transform='" + transformFromSource(source.leftArmLeft, x, scale, scale) + "'>",
        "<g transform='matrix(1,0,0,1,7.5,0)'>",
        "<rect x='164' y='596' width='21' height='60.5' style='" + style + "'/>",
        "</g>",
        "</g>"
      ].join("");
    }

    function rightLeg() {
      return [
        "<g transform='" + transformFromSource(source.rightArmLeft, x + width - fixedArmWidth, scale, scale) + "'>",
        "<g transform='matrix(1,0,0,1,350.5,0)'>",
        "<rect x='164' y='596' width='21' height='60.5' style='" + style + "'/>",
        "</g>",
        "</g>"
      ].join("");
    }

    function seatBase() {
      const top = yFromSource(source.seatBaseTop);
      const baseHeight = source.seatBaseHeight * scale;
      const radius = 7 * scale;

      return "<path d='" + roundedRectPath(contentX, top, contentWidth, baseHeight, radius) + "' style='" + style + "'/>";
    }

    function legHorizontalBar() {
      const left = x + ((source.legBarLeft - source.leftArmLeft) * scale);
      const right = x + width - ((source.rightArmLeft + source.leftArmWidth - source.legBarLeft - 322) * scale);
      const top = yFromSource(source.legBarTop);
      const barHeight = source.legBarHeight * scale;

      return "<rect x='" + left.toFixed(1) + "' y='" + top.toFixed(1) + "' width='" + Math.max(4, right - left).toFixed(1) + "' height='" + barHeight.toFixed(1) + "' style='" + style + "'/>";
    }

    return [
      "<g stroke-linecap='round' stroke-linejoin='round' data-sofa-seat-count='" + seatCount + "'>",
      Array.from({ length: seatCount }, function (_, index) {
        return cushionModule(index);
      }).join(""),
      seatBase(),
      legHorizontalBar(),
      leftArm(),
      rightArm(),
      leftLeg(),
      rightLeg(),
      "</g>"
    ].join("");
  }

  function bedMarkup(x, y, width, height, hasHeadboard) {
    const bedFill = COLOURS.furnitureFill;
    const mattressFill = "#F7F7F7";
    const mainStroke = FURNITURE_STROKE;
    const source = hasHeadboard === false
      ? { x: 772, y: 1060, width: 2619, height: 625 }
      : { x: 772, y: 575, width: 2619, height: 1110 };
    const scaleX = width / source.width;
    const scaleY = height / source.height;

    function px(value) {
      return x + ((value - source.x) * scaleX);
    }

    function py(value) {
      return y + ((value - source.y) * scaleY);
    }

    function n(value) {
      return value.toFixed(1);
    }

    function rectFromSource(rectX, rectY, rectWidth, rectHeight, fill) {
      return "<rect x='" + n(px(rectX)) + "' y='" + n(py(rectY)) + "' width='" + n(rectWidth * scaleX) + "' height='" + n(rectHeight * scaleY) + "' fill='" + fill + "'/>";
    }

    function headboardPath() {
      return [
        "M", n(px(3220)), n(py(1098)),
        "L", n(px(3220)), n(py(615)),
        "C", n(px(3220)), n(py(615)), n(px(3222.476)), n(py(575.19)), n(px(3181)), n(py(575)),
        "C", n(px(3139.524)), n(py(574.81)), n(px(986)), n(py(575)), n(px(986)), n(py(575)),
        "C", n(px(986)), n(py(575)), n(px(950.23)), n(py(572.682)), n(px(950)), n(py(615)),
        "C", n(px(949.77)), n(py(657.318)), n(px(950)), n(py(1099)), n(px(950)), n(py(1099)),
        "L", n(px(3220)), n(py(1098)),
        "Z"
      ].join(" ");
    }

    function mattressTopPath() {
      return [
        "M", n(px(897)), n(py(1147)),
        "C", n(px(897)), n(py(1147)), n(px(913.78)), n(py(1087.761)), n(px(1050)), n(py(1073)),
        "C", n(px(1186.22)), n(py(1058.239)), n(px(2919.991)), n(py(1059.617)), n(px(2945)), n(py(1060)),
        "C", n(px(2970.009)), n(py(1060.383)), n(px(3122.683)), n(py(1058.413)), n(px(3199)), n(py(1089)),
        "C", n(px(3275.317)), n(py(1119.587)), n(px(3268)), n(py(1146)), n(px(3268)), n(py(1146)),
        "L", n(px(897)), n(py(1147)),
        "Z"
      ].join(" ");
    }

    function mattressBodyPath() {
      function tx(value) {
        return value - 70.595935;
      }

      function ty(value) {
        return value + 11;
      }

      return [
        "M", n(px(tx(2173))), n(py(ty(1125))),
        "L", n(px(tx(1003))), n(py(ty(1131))),
        "C", n(px(tx(1003))), n(py(ty(1131))), n(px(tx(893.551))), n(py(ty(1135.746))), n(px(tx(885))), n(py(ty(1203))),
        "C", n(px(tx(876.449))), n(py(ty(1270.254))), n(px(tx(877))), n(py(ty(1363))), n(px(tx(877))), n(py(ty(1363))),
        "C", n(px(tx(877))), n(py(ty(1363))), n(px(tx(873.091))), n(py(ty(1409.361))), n(px(tx(895))), n(py(ty(1413))),
        "C", n(px(tx(916.909))), n(py(ty(1416.639))), n(px(tx(3412))), n(py(ty(1413))), n(px(tx(3412))), n(py(ty(1413))),
        "C", n(px(tx(3412))), n(py(ty(1413))), n(px(tx(3432.956))), n(py(ty(1414.514))), n(px(tx(3431))), n(py(ty(1365))),
        "C", n(px(tx(3429.044))), n(py(ty(1315.486))), n(px(tx(3430))), n(py(ty(1223))), n(px(tx(3430))), n(py(ty(1223))),
        "C", n(px(tx(3430))), n(py(ty(1223))), n(px(tx(3418.134))), n(py(ty(1139.525))), n(px(tx(3337))), n(py(ty(1133))),
        "C", n(px(tx(3255.866))), n(py(ty(1126.475))), n(px(tx(2173))), n(py(ty(1125))), n(px(tx(2173))), n(py(ty(1125))),
        "Z"
      ].join(" ");
    }

    function basePath() {
      return [
        "M", n(px(2082.595)), n(py(1425.618)),
        "L", n(px(793)), n(py(1425.618)),
        "C", n(px(793)), n(py(1425.618)), n(px(771.791)), n(py(1427.194)), n(px(772)), n(py(1449)),
        "C", n(px(772.209)), n(py(1470.806)), n(px(772)), n(py(1612)), n(px(772)), n(py(1612)),
        "C", n(px(772)), n(py(1612)), n(px(772.619)), n(py(1630.196)), n(px(792)), n(py(1631)),
        "C", n(px(811.381)), n(py(1631.804)), n(px(3375)), n(py(1631)), n(px(3375)), n(py(1631)),
        "C", n(px(3375)), n(py(1631)), n(px(3391.583)), n(py(1630.44)), n(px(3391)), n(py(1609)),
        "C", n(px(3390.417)), n(py(1587.56)), n(px(3391)), n(py(1449)), n(px(3391)), n(py(1449)),
        "C", n(px(3391)), n(py(1449)), n(px(3393.56)), n(py(1426.647)), n(px(3370)), n(py(1425.618)),
        "C", n(px(3346.44)), n(py(1424.588)), n(px(2082.595)), n(py(1425.618)), n(px(2082.595)), n(py(1425.618)),
        "Z"
      ].join(" ");
    }

    return [
      "<g fill='none' stroke='" + FURNITURE_OUTLINE + "' stroke-width='" + mainStroke.toFixed(2) + "' vector-effect='non-scaling-stroke' stroke-linecap='round' stroke-linejoin='round'>",
      rectFromSource(785, 1631.357, 120, 53.643, bedFill),
      rectFromSource(3262, 1631.357, 120, 53.643, bedFill),
      hasHeadboard === false ? "" : "<path d='" + headboardPath() + "' fill='" + bedFill + "'/>",
      "<path d='" + mattressTopPath() + "' fill='" + mattressFill + "'/>",
      "<path d='" + mattressBodyPath() + "' fill='" + mattressFill + "'/>",
      "<path d='" + basePath() + "' fill='" + bedFill + "'/>",
      "</g>"
    ].join("");
  }

  function bedsideTableMarkup(x, y, width, height, strokeWidth) {
    const furnitureFill = COLOURS.furnitureFill;
    const source = {
      x: 392,
      y: 34,
      width: 217,
      height: 382
    };
    const scaleX = width / source.width;
    const scaleY = height / source.height;
    const transform = [
      "translate(" + x.toFixed(2) + " " + y.toFixed(2) + ")",
      "scale(" + scaleX.toFixed(4) + " " + scaleY.toFixed(4) + ")",
      "translate(" + (-source.x).toFixed(2) + " " + (-source.y).toFixed(2) + ")"
    ].join(" ");

    return [
      "<g transform='" + transform + "' fill='" + furnitureFill + "' stroke='" + FURNITURE_OUTLINE + "' stroke-width='" + strokeWidth.toFixed(2) + "' vector-effect='non-scaling-stroke' stroke-linecap='round' stroke-linejoin='bevel'>",
      "<path d='M399,381L392,416L406,416L417,381L399,381Z'/>",
      "<g transform='matrix(-1,0,0,1,1001,0)'><path d='M399,381L392,416L406,416L417,381L399,381Z'/></g>",
      "<rect x='392' y='210' width='217' height='170.579'/>",
      "<path d='M405,224L595,224L595,369L406,369L405,224Z' fill='none'/>",
      "<path d='M405.5,296.5L595,296.5' fill='none'/>",
      "<rect x='473' y='259' width='55' height='9' fill='none'/>",
      "<g transform='matrix(1,0,0,1,0,71)'><rect x='473' y='259' width='55' height='9' fill='none'/></g>",
      "<g transform='matrix(1,0,0,1,-25.5,0)' fill='none'>",
      "<path d='M525,201L547,201C547,201 578.316,156.426 541,135'/>",
      "<path d='M547,210L547,201'/>",
      "<path d='M541,135L541,125'/>",
      "<path d='M525,125L541,125'/>",
      "<path d='M530,125L530,119L579,119L559.298,34L525,34'/>",
      "<g transform='matrix(-1,0,0,1,1051,0)'><path d='M525,201L547,201C547,201 578.316,156.426 541,135'/></g>",
      "<g transform='matrix(-1,0,0,1,1051,0)'><path d='M547,210L547,201'/></g>",
      "<g transform='matrix(-1,0,0,1,1051,0)'><path d='M541,135L541,125'/></g>",
      "<g transform='matrix(-1,0,0,1,1051,0)'><path d='M525,125L541,125'/></g>",
      "<g transform='matrix(-1,0,0,1,1051,0)'><path d='M530,125L530,119L579,119L559.298,34L525,34'/></g>",
      "</g>",
      "</g>"
    ].join("");
  }

  function bedsideTablesMarkup(geometry, bedX, bedWidth) {
    const tableWidth = 56 * geometry.scale;
    const tableHeight = tableWidth * (382 / 217);
    const gap = 14 * geometry.scale;
    const y = geometry.floorY - tableHeight;
    const leftX = bedX - gap - tableWidth;
    const rightX = bedX + bedWidth + gap;
    const wallRight = geometry.wallLeft + geometry.wallWidthPx;
    const strokeWidth = FURNITURE_STROKE;
    const tables = [];

    if (leftX >= geometry.wallLeft + 4) {
      tables.push(bedsideTableMarkup(leftX, y, tableWidth, tableHeight, strokeWidth));
    }

    if (rightX + tableWidth <= wallRight - 4) {
      tables.push(bedsideTableMarkup(rightX, y, tableWidth, tableHeight, strokeWidth));
    }

    return tables.join("");
  }

  function consoleMarkup(x, y, width, height) {
    const source = {
      x: 27.132,
      y: 198,
      width: 945.736,
      height: 606.874
    };
    const scaleX = width / source.width;
    const scaleY = height / source.height;
    const strokeStyle = FURNITURE_STROKE_STYLE;
    const fillStyle = FURNITURE_FILL_STYLE;
    const topFillStyle = FURNITURE_FILL_STYLE;

    function px(value) {
      return x + ((value - source.x) * scaleX);
    }

    function py(value) {
      return y + ((value - source.y) * scaleY);
    }

    function n(value) {
      return value.toFixed(1);
    }

    return [
      "<g stroke-linecap='round' stroke-linejoin='round'>",
      "<path d='M" + n(px(111)) + "," + n(py(342)) + "L" + n(px(67)) + "," + n(py(804.874)) + "L" + n(px(90)) + "," + n(py(804.874)) + "L" + n(px(154)) + "," + n(py(342)) + "' style='" + fillStyle + strokeStyle + "'/>",
      "<path d='M" + n(px(901)) + "," + n(py(342)) + "L" + n(px(945)) + "," + n(py(804.874)) + "L" + n(px(922)) + "," + n(py(804.874)) + "L" + n(px(858)) + "," + n(py(342)) + "' style='" + fillStyle + strokeStyle + "'/>",
      "<rect x='" + n(px(27.132)) + "' y='" + n(py(198)) + "' width='" + n(945.736 * scaleX) + "' height='" + n(144 * scaleY) + "' style='" + topFillStyle + strokeStyle + "'/>",
      "<path d='M" + n(px(972.868)) + "," + n(py(342)) +
        "L" + n(px(948)) + "," + n(py(324)) +
        "L" + n(px(55)) + "," + n(py(324)) +
        "L" + n(px(27.132)) + "," + n(py(342)) +
        "L" + n(px(55)) + "," + n(py(324)) +
        "L" + n(px(55)) + "," + n(py(216)) +
        "L" + n(px(27.132)) + "," + n(py(198)) +
        "L" + n(px(55)) + "," + n(py(216)) +
        "L" + n(px(949)) + "," + n(py(216)) +
        "L" + n(px(972.868)) + "," + n(py(198)) +
        "L" + n(px(949)) + "," + n(py(216)) +
        "L" + n(px(949)) + "," + n(py(324)) +
        "L" + n(px(504.5)) + "," + n(py(324)) +
        "L" + n(px(504.5)) + "," + n(py(216)) + "' style='fill:none;" + strokeStyle + "'/>",
      "</g>"
    ].join("");
  }

  function diningTableMarkup(x, y, width, height, scale) {
    const strokeWidth = FURNITURE_STROKE;
    const topHeight = clamp(7 * scale, 5, 10);
    const topY = y + height * 0.06;
    const sideLineY = topY + topHeight + clamp(10 * scale, 7, 14);
    const legTopY = topY + topHeight;
    const legBottomY = y + height;
    const legWidth = clamp(8 * scale, 5, 10);
    const legInset = clamp(24 * scale, 18, 42);
    const leftLegX = x + legInset;
    const rightLegX = x + width - legInset - legWidth;
    const leftLegInside = leftLegX + legWidth;
    const rightLegInside = rightLegX;

    function leg(legX) {
      return [
        "<path d='M " + legX.toFixed(1) + " " + legTopY.toFixed(1),
        "L " + legX.toFixed(1) + " " + legBottomY.toFixed(1),
        "L " + (legX + legWidth).toFixed(1) + " " + legBottomY.toFixed(1),
        "L " + (legX + legWidth).toFixed(1) + " " + legTopY.toFixed(1),
        "Z' fill='" + COLOURS.furnitureFill + "'/>"
      ].join(" ");
    }

    function chairSvg(cx, cy, scale) {
      const sourceStrokeWidth = strokeWidth / scale;
      return [
        "<g transform='translate(" + cx.toFixed(1) + " " + cy.toFixed(1) + ") scale(" + scale.toFixed(3) + ") translate(-98 -278)' fill='" + COLOURS.furnitureFill + "' stroke='" + FURNITURE_OUTLINE + "' stroke-width='" + sourceStrokeWidth.toFixed(2) + "' vector-effect='non-scaling-stroke' stroke-linecap='round' stroke-linejoin='round'>",
        "<path d='M 98 0 L 18 1 C 18 1, 11 1, 10 10 C 9 18, 7 64, 7 64 C 7 64, 7 71, 16 72 C 26 73, 69 77, 98 77 C 127 77, 170 73, 180 72 C 189 71, 189 64, 189 64 C 189 64, 187 18, 186 10 C 185 1, 178 1, 178 1 Z'/>",
        "<path d='M 98 145 C 98 145, 22 145, 7 138 C -7 131, 6 122, 16 124 C 25 126, 82 128, 98 128 C 114 128, 171 126, 180 124 C 190 122, 203 131, 189 138 C 174 145, 98 145, 98 145 Z'/>",
        "<path d='M 18 72 L 0 278 L 14 278 L 29 75 Z'/>",
        "<path d='M 178 72 L 196 278 L 182 278 L 167 75 Z'/>",
        "</g>"
      ].join("");
    }

    function chairsMarkup() {
      const chairWidthCm = 48;
      const chairSourceWidth = 196;
      const chairScale = (chairWidthCm * scale) / chairSourceWidth;
      const chairWidth = chairSourceWidth * chairScale;
      const chairY = legBottomY;
      const availableWidth = rightLegInside - leftLegInside;
      const minimumGap = 14 * scale;
      const maxThatFits = Math.floor((availableWidth + minimumGap) / (chairWidth + minimumGap));
      const chairCount = clamp(maxThatFits, 1, 5);
      const equalGap = (availableWidth - chairWidth * chairCount) / (chairCount + 1);

      return Array.from({ length: chairCount }, function(_, index) {
        return chairSvg(
          leftLegInside + equalGap * (index + 1) + chairWidth * index + chairWidth / 2,
          chairY,
          chairScale
        );
      }).join("");
    }

    return [
      "<g>",
      "<g fill='none' stroke='" + FURNITURE_OUTLINE + "' stroke-width='" + strokeWidth.toFixed(2) + "' vector-effect='non-scaling-stroke' stroke-linecap='square' stroke-linejoin='bevel'>",
      leg(rightLegX),
      leg(leftLegX),
      "<rect x='" + x.toFixed(1) + "' y='" + topY.toFixed(1) + "' width='" + width.toFixed(1) + "' height='" + topHeight.toFixed(1) + "' fill='" + COLOURS.furnitureFill + "'/>",
      "<path d='M " + leftLegInside.toFixed(1) + " " + sideLineY.toFixed(1) + " L " + rightLegInside.toFixed(1) + " " + sideLineY.toFixed(1) + "'/>",
      "</g>",
      chairsMarkup(),
      "</g>"
    ].join("");
  }

  function bathMarkup(x, y, width, height) {
    const source = {
      x: 64,
      y: 338,
      width: 877,
      height: 318
    };
    const scaleX = width / source.width;
    const scaleY = height / source.height;
    const transform = "translate(" + x.toFixed(1) + " " + y.toFixed(1) + ") scale(" + scaleX.toFixed(4) + " " + scaleY.toFixed(4) + ") translate(" + (-source.x).toFixed(1) + " " + (-source.y).toFixed(1) + ")";
    const strokeStyle = FURNITURE_LINE_STYLE;
    const fillStyle = "fill:" + COLOURS.furnitureFill + ";stroke:none;";
    const footFillPath = "M811,546C811,546 799.573,544.001 794,534C788.427,523.999 777.351,512.874 772,528C766.649,543.126 758.49,555.474 743,556C727.51,556.526 740.797,559.599 730.303,566C719.809,572.401 714.35,570.592 721,585C727.65,599.408 730.607,590.434 740,602C749.393,613.566 739.94,636.318 755,650C770.06,663.682 776.469,662.984 778,656C779.531,649.016 789.954,648.16 790,637C790.046,625.84 765.443,621.553 773,599C780.557,576.447 792.958,577.611 804,568C815.042,558.389 813.635,555.973 813,549Z";

    return [
      "<g transform='" + transform + "' fill-rule='evenodd' clip-rule='evenodd' stroke-linecap='round' stroke-linejoin='round' stroke-miterlimit='1.5'>",
      "<path d='M65.002,338.12L940,338C940,338 960.84,338.603 960.604,354C960.505,360.399 958.001,368.595 943,370C927.999,371.405 911,371.054 911,371.054L811,546C788,560 755,570 718.093,575.73L500,575.73L286.909,575.85C250,570 217,560 194.002,546.12L94.002,371.174C94.002,371.174 77.003,371.525 62.002,370.12C47.001,368.715 44.497,360.519 44.398,354.12C44.162,338.723 65.002,338.12 65.002,338.12Z' style='" + fillStyle + "'/>",
      "<path d='" + footFillPath + "' style='" + fillStyle + "'/>",
      "<g transform='matrix(-1,0,0,1,1005.001999,0.120528)'>",
      "<path d='" + footFillPath + "' style='" + fillStyle + "'/>",
      "</g>",
      "<g>",
      "<path d='M718.093,575.73L500,575.73' style='" + strokeStyle + "'/>",
      "<g transform='matrix(-1,0,0,1,1005.001999,0.120528)'>",
      "<path d='M718.093,575.73L500,575.73' style='" + strokeStyle + "'/>",
      "</g>",
      "</g>",
      "<path d='M911,371.054L811,546C811,546 799.573,544.001 794,534C788.427,523.999 777.351,512.874 772,528C766.649,543.126 758.49,555.474 743,556C727.51,556.526 740.797,559.599 730.303,566C719.809,572.401 714.35,570.592 721,585C727.65,599.408 730.607,590.434 740,602C749.393,613.566 739.94,636.318 755,650C770.06,663.682 776.469,662.984 778,656C779.531,649.016 789.954,648.16 790,637C790.046,625.84 765.443,621.553 773,599C780.557,576.447 792.958,577.611 804,568C815.042,558.389 813.635,555.973 813,549' style='" + strokeStyle + "'/>",
      "<g transform='matrix(-1,0,0,1,1005.001999,0.120528)'>",
      "<path d='M911,371.054L811,546C811,546 799.573,544.001 794,534C788.427,523.999 777.351,512.874 772,528C766.649,543.126 758.49,555.474 743,556C727.51,556.526 740.797,559.599 730.303,566C719.809,572.401 714.35,570.592 721,585C727.65,599.408 730.607,590.434 740,602C749.393,613.566 739.94,636.318 755,650C770.06,663.682 776.469,662.984 778,656C779.531,649.016 789.954,648.16 790,637C790.046,625.84 765.443,621.553 773,599C780.557,576.447 792.958,577.611 804,568C815.042,558.389 813.635,555.973 813,549' style='" + strokeStyle + "'/>",
      "</g>",
      "<g>",
      "<g transform='matrix(-1,0,0,1,1005.001999,0.120528)'>",
      "<path d='M500,338L940,338C940,338 960.84,338.603 960.604,354C960.505,360.399 958.001,368.595 943,370C927.999,371.405 500,371 500,371' style='" + strokeStyle + "'/>",
      "</g>",
      "<path d='M500,338L940,338C940,338 960.84,338.603 960.604,354C960.505,360.399 958.001,368.595 943,370C927.999,371.405 500,371 500,371' style='" + strokeStyle + "'/>",
      "</g>",
      "</g>"
    ].join("");
  }

  function vanityMarkup(x, y, width, height, scale) {
    const sourceLeft = 270.8;
    const sourceTop = 189;
    const sourceWidth = 458.4;
    const defaultVanityWidthCm = 80;
    const vanityWidthCm = width / scale;
    const isDoubleVanity = vanityWidthCm >= 140;
    const maxFixtureWidth = isDoubleVanity
      ? Math.min(scale * defaultVanityWidthCm, (width / 2) * 0.9)
      : scale * defaultVanityWidthCm;
    const fixtureScale = maxFixtureWidth / sourceWidth;
    const fixtureWidth = sourceWidth * fixtureScale;
    const fixtureCentres = isDoubleVanity
      ? [x + (width * 0.25), x + (width * 0.75)]
      : [x + (width / 2)];
    const bodyOffsetY = (298 - sourceTop) * fixtureScale;
    const bodyY = y + bodyOffsetY;
    const bodyHeight = Math.max(0, height - bodyOffsetY);
    const upperLineY = bodyY + (bodyHeight * 0.232);
    const lowerLineY = bodyY + (bodyHeight * 0.789);
    const centreLineX = x + (width / 2);
    const bodyStyle = FURNITURE_FILL_STYLE + FURNITURE_STROKE_STYLE;
    const basinStyle = FURNITURE_FILL_STYLE + FURNITURE_STROKE_STYLE;
    const lineStyle = FURNITURE_LINE_STYLE;
    const fixtureStyle = FURNITURE_FILL_STYLE + FURNITURE_STROKE_STYLE;

    function fixtureMarkup(fixtureCentre) {
      const fixtureLeft = fixtureCentre - (fixtureWidth / 2);
      const fixtureTransform = "translate(" + fixtureLeft.toFixed(1) + " " + y.toFixed(1) + ") scale(" + fixtureScale.toFixed(4) + ") translate(" + (-sourceLeft).toFixed(1) + " " + (-sourceTop).toFixed(1) + ")";

      return [
        "<g transform='" + fixtureTransform + "' fill-rule='evenodd' clip-rule='evenodd' stroke-linecap='round' stroke-linejoin='round' stroke-miterlimit='1.5'>",
      "<path d='M334,274L334,358L666,358L666,272L334,274Z' style='" + basinStyle + "'/>",
      "<g>",
      "<g>",
      "<g transform='matrix(1,0,0,1,-1,-3)'>",
      "<circle cx='444' cy='214' r='13' style='" + fixtureStyle + "'/>",
      "</g>",
      "<path d='M447,191L447,234C447,235.104 446.104,236 445,236L441,236C439.896,236 439,235.104 439,234L439,191C439,189.896 439.896,189 441,189L445,189C446.104,189 447,189.896 447,191Z' style='" + fixtureStyle + "'/>",
      "<g transform='matrix(0,1,-1,0,655.5,-230.5)'>",
      "<path d='M447,191L447,234C447,235.104 446.104,236 445,236L441,236C439.896,236 439,235.104 439,234L439,191C439,189.896 439.896,189 441,189L445,189C446.104,189 447,189.896 447,191Z' style='" + fixtureStyle + "'/>",
      "</g>",
      "<g transform='matrix(1,0,0,1,-1,-4.5)'>",
      "<circle cx='444' cy='216' r='5' style='" + fixtureStyle + "'/>",
      "</g>",
      "</g>",
      "<g transform='matrix(1,0,0,1,118,0)'>",
      "<g transform='matrix(1,0,0,1,-1,-3)'>",
      "<circle cx='444' cy='214' r='13' style='" + fixtureStyle + "'/>",
      "</g>",
      "<path d='M447,191L447,234C447,235.104 446.104,236 445,236L441,236C439.896,236 439,235.104 439,234L439,191C439,189.896 439.896,189 441,189L445,189C446.104,189 447,189.896 447,191Z' style='" + fixtureStyle + "'/>",
      "<g transform='matrix(0,1,-1,0,655.5,-230.5)'>",
      "<path d='M447,191L447,234C447,235.104 446.104,236 445,236L441,236C439.896,236 439,235.104 439,234L439,191C439,189.896 439.896,189 441,189L445,189C446.104,189 447,189.896 447,191Z' style='" + fixtureStyle + "'/>",
      "</g>",
      "<g transform='matrix(1,0,0,1,-1,-4.5)'>",
      "<circle cx='444' cy='216' r='5' style='" + fixtureStyle + "'/>",
      "</g>",
      "</g>",
      "<g>",
      "<g transform='matrix(1,0,0,1,-0.497533,5)'>",
      "<circle cx='501.5' cy='207.5' r='18.5' style='" + fixtureStyle + "'/>",
      "</g>",
      "<path d='M495,212.5L495,249L507,249L507,212.5C507,212.5 507.42,203.101 500.5,204C493.58,204.899 495.493,211.802 495,212.5Z' style='" + fixtureStyle + "'/>",
      "</g>",
      "</g>",
      "</g>"
      ].join("");
    }

    return [
      "<g fill-rule='evenodd' clip-rule='evenodd' stroke-linecap='square' stroke-linejoin='round' stroke-miterlimit='1.5'>",
      "<rect x='" + x.toFixed(1) + "' y='" + bodyY.toFixed(1) + "' width='" + width.toFixed(1) + "' height='" + bodyHeight.toFixed(1) + "' style='" + bodyStyle + "' vector-effect='non-scaling-stroke'/>",
      "<path d='M" + x.toFixed(1) + "," + upperLineY.toFixed(1) + "L" + (x + width).toFixed(1) + "," + upperLineY.toFixed(1) + "' style='" + lineStyle + "'/>",
      "<path d='M" + x.toFixed(1) + "," + lowerLineY.toFixed(1) + "L" + (x + width).toFixed(1) + "," + lowerLineY.toFixed(1) + "' style='" + lineStyle + "'/>",
      "<path d='M" + centreLineX.toFixed(1) + "," + upperLineY.toFixed(1) + "L" + centreLineX.toFixed(1) + "," + lowerLineY.toFixed(1) + "' style='" + lineStyle + "'/>",
      "</g>",
      fixtureCentres.map(fixtureMarkup).join("")
    ].join("");
  }

  function genericFurnitureMarkup(x, y, width, height) {
    return [
      "<rect x='" + x.toFixed(1) + "' y='" + y.toFixed(1) + "' width='" + width.toFixed(1) + "' height='" + height.toFixed(1) + "' rx='5' style='" + FURNITURE_FILL_STYLE + FURNITURE_STROKE_STYLE + "'/>"
    ].join("");
  }

  function furnitureMarkupByType(anchor, x, y, width, height, scale, engineResult) {
    if (anchor.type === "sofa") {
      return sofaMarkup(x, y, width, height, getSofaSeatCount(engineResult, anchor.widthCm));
    }

    if (anchor.type === "bed") {
      return bedMarkup(x, y, width, height, anchor.hasHeadboard);
    }

    if (anchor.type === "console") {
      return consoleMarkup(x, y, width, height);
    }

    if (anchor.type === "desk") {
      return diningTableMarkup(x, y, width, height, scale);
    }

    if (anchor.type === "vanity") {
      return vanityMarkup(x, y, width, height, scale);
    }

    if (anchor.type === "bath") {
      return bathMarkup(x, y, width, height);
    }

    return genericFurnitureMarkup(x, y, width, height);
  }

  function defineGeometry(engineResult) {
    const previewGeometry = engineResult.previewGeometry;
    const wall = previewGeometry.wall;
    const artwork = previewGeometry.artwork;
    const contentWidth = VIEWBOX_WIDTH - (SIDE_MARGIN * 2);
    const contentHeight = VIEWBOX_HEIGHT - TOP_MARGIN - BOTTOM_MARGIN;
    const scale = Math.min(
      contentWidth / wall.wallWidthCm,
      contentHeight / wall.ceilingHeightCm
    );
    const wallWidthPx = wall.wallWidthCm * scale;
    const wallHeightPx = wall.ceilingHeightCm * scale;
    const wallLeft = (VIEWBOX_WIDTH - wallWidthPx) / 2;
    const floorY = VIEWBOX_HEIGHT - BOTTOM_MARGIN;
    const wallTop = floorY - wallHeightPx;
    const floorLineY = floorY + 10;
    const outerLeft = Math.max(10, wallLeft - 18);
    const outerTop = Math.max(10, wallTop - 46);
    const outerRight = Math.min(VIEWBOX_WIDTH - 10, wallLeft + wallWidthPx + 18);
    const outerBottom = Math.min(VIEWBOX_HEIGHT - 8, floorLineY + 12);
    const viewBoxPadding = 12;
    const viewBoxLeft = Math.max(0, outerLeft - viewBoxPadding);
    const viewBoxTop = Math.max(0, outerTop - viewBoxPadding);
    const viewBoxRight = Math.min(VIEWBOX_WIDTH, outerRight + viewBoxPadding);
    const viewBoxBottom = Math.min(VIEWBOX_HEIGHT, outerBottom + viewBoxPadding);

    function xFromCm(cm) {
      return wallLeft + (cm * scale);
    }

    function yFromFloor(cm) {
      return floorY - (cm * scale);
    }

    return {
      engineResult: engineResult,
      recommendation: engineResult.recommendation,
      wall: wall,
      anchor: previewGeometry.anchor,
      artwork: artwork,
      scale: scale,
      wallLeft: wallLeft,
      wallTop: wallTop,
      wallWidthPx: wallWidthPx,
      wallHeightPx: wallHeightPx,
      viewBoxLeft: viewBoxLeft,
      viewBoxTop: viewBoxTop,
      viewBoxWidth: viewBoxRight - viewBoxLeft,
      viewBoxHeight: viewBoxBottom - viewBoxTop,
      floorY: floorY,
      xFromCm: xFromCm,
      yFromFloor: yFromFloor,
      artworkLeftPx: xFromCm(artwork.groupCentreXCm - (artwork.totalArtworkWidthCm / 2)),
      artworkRightPx: xFromCm(artwork.groupCentreXCm + (artwork.totalArtworkWidthCm / 2)),
      artworkTopPx: yFromFloor(artwork.topFromFloorCm),
      artworkBottomPx: yFromFloor(artwork.bottomFromFloorCm),
      artworkCentreXPx: xFromCm(artwork.groupCentreXCm),
      artworkCentreYPx: yFromFloor(artwork.centreFromFloorCm)
    };
  }

  function drawStructure(geometry) {
    const usableLeftX = geometry.xFromCm(geometry.wall.usableWallLeftLimitCm);
    const usableRightX = geometry.xFromCm(geometry.wall.usableWallRightLimitCm);
    const hasFurniture = geometry.anchor && geometry.anchor.type !== "none";

    return [
      hasFurniture ? "<line x1='" + geometry.wallLeft.toFixed(1) + "' y1='" + geometry.floorY.toFixed(1) + "' x2='" + (geometry.wallLeft + geometry.wallWidthPx).toFixed(1) + "' y2='" + geometry.floorY.toFixed(1) + "' stroke='" + COLOURS.wallStroke + "' stroke-width='0.75' opacity='0.35'/>" : "",
      "<line x1='" + usableLeftX.toFixed(1) + "' y1='" + geometry.wallTop.toFixed(1) + "' x2='" + usableLeftX.toFixed(1) + "' y2='" + geometry.floorY.toFixed(1) + "' stroke='" + COLOURS.usableGuide + "' stroke-width='0.55' stroke-dasharray='3 7' opacity='0.22'/>",
      "<line x1='" + usableRightX.toFixed(1) + "' y1='" + geometry.wallTop.toFixed(1) + "' x2='" + usableRightX.toFixed(1) + "' y2='" + geometry.floorY.toFixed(1) + "' stroke='" + COLOURS.usableGuide + "' stroke-width='0.55' stroke-dasharray='3 7' opacity='0.22'/>"
    ].join("");
  }

  function drawFurniture(geometry) {
    const anchor = geometry.anchor;

    if (
      !anchor ||
      anchor.type === "none" ||
      anchor.widthCm == null ||
      anchor.topHeightCm == null ||
      anchor.centreXCm == null
    ) {
      return "";
    }

    const leftCm = anchor.centreXCm - (anchor.widthCm / 2);
    const x = geometry.xFromCm(leftCm);
    const y = geometry.yFromFloor(anchor.topHeightCm);
    const width = anchor.widthCm * geometry.scale;
    const height = anchor.topHeightCm * geometry.scale;

    if (anchor.type === "bed") {
      return [
        bedsideTablesMarkup(geometry, x, width),
        furnitureMarkupByType(anchor, x, y, width, height, geometry.scale, geometry.engineResult)
      ].join("");
    }

    return furnitureMarkupByType(anchor, x, y, width, height, geometry.scale, geometry.engineResult);
  }

  function drawArtwork(geometry) {
    const colours = FRAME_COLOURS;
    const frameThicknessPx = Math.max(1.5, FRAME_THICKNESS_CM * geometry.scale);
    const mountInsetPx = Math.max(5, frameThicknessPx * 2.7);

    return geometry.artwork.pieces.map(function (piece) {
      const x = geometry.xFromCm(piece.leftCm);
      const y = geometry.yFromFloor(piece.topFromFloorCm);
      const width = piece.outerWidthCm * geometry.scale;
      const height = piece.outerHeightCm * geometry.scale;
      const innerInsetX = Math.min(width / 3, frameThicknessPx);
      const innerInsetY = Math.min(height / 3, frameThicknessPx);
      const innerWidth = Math.max(2, width - (innerInsetX * 2));
      const innerHeight = Math.max(2, height - (innerInsetY * 2));
      const paperInsetX = Math.min(innerWidth / 3, mountInsetPx);
      const paperInsetY = Math.min(innerHeight / 3, mountInsetPx);
      const paperWidth = Math.max(2, innerWidth - (paperInsetX * 2));
      const paperHeight = Math.max(2, innerHeight - (paperInsetY * 2));
      const innerStrokePx = 0.8;

      return [
        "<g>",
        "<rect x='" + x.toFixed(1) + "' y='" + y.toFixed(1) + "' width='" + width.toFixed(1) + "' height='" + height.toFixed(1) + "' fill='" + colours.outer + "' stroke='" + colours.stroke + "' stroke-width='" + ART_STROKE.toFixed(1) + "'/>",
        "<rect x='" + (x + innerInsetX).toFixed(1) + "' y='" + (y + innerInsetY).toFixed(1) + "' width='" + innerWidth.toFixed(1) + "' height='" + innerHeight.toFixed(1) + "' fill='" + colours.mount + "'/>",
        "<rect x='" + (x + innerInsetX + paperInsetX).toFixed(1) + "' y='" + (y + innerInsetY + paperInsetY).toFixed(1) + "' width='" + paperWidth.toFixed(1) + "' height='" + paperHeight.toFixed(1) + "' fill='" + colours.paper + "' stroke='" + colours.innerLine + "' stroke-width='" + innerStrokePx.toFixed(1) + "'/>",
        "</g>"
      ].join("");
    }).join("");
  }

  function drawMeasurements(geometry) {
    const measurementMarkup = [];
    const widthLineY = clamp(geometry.artworkTopPx - 22, geometry.wallTop + 20, geometry.artworkTopPx - 10);
    const gapLineX = clamp(
      geometry.artworkRightPx + 18,
      geometry.wallLeft + 34,
      geometry.wallLeft + geometry.wallWidthPx - 34
    );

    measurementMarkup.push(widthDimensionLine(
      geometry.artworkLeftPx,
      geometry.artworkRightPx,
      widthLineY,
      geometry.artwork.totalArtworkWidthCm
    ));

    if (
      geometry.anchor &&
      geometry.anchor.type !== "none" &&
      geometry.artwork.clearanceAboveFurnitureCm != null &&
      geometry.anchor.topHeightCm != null
    ) {
      measurementMarkup.push(gapDimensionLine(
        gapLineX,
        geometry.yFromFloor(geometry.anchor.topHeightCm),
        geometry.artworkBottomPx,
        geometry.artwork.clearanceAboveFurnitureCm
      ));
    }

    return measurementMarkup.join("");
  }

  function drawLabels(geometry) {
    const crosshairHalfHeight = Math.min(11, geometry.artwork.totalArtworkHeightCm * geometry.scale * 0.08);
    const centreDotRadius = 2.4;
    const fullLabelWidth = 170;
    const compactLabelWidth = 105;
    const labelGap = 22;
    const roomLeft = geometry.wallLeft + 26;
    const roomRight = geometry.wallLeft + geometry.wallWidthPx - 26;
    const rightSpace = roomRight - geometry.artworkRightPx;
    const leftSpace = geometry.artworkLeftPx - roomLeft;
    const useRight = rightSpace >= fullLabelWidth + labelGap || rightSpace >= leftSpace;
    const sideSpace = useRight ? rightSpace : leftSpace;
    const compact = sideSpace < fullLabelWidth + labelGap;
    const neededWidth = compact ? compactLabelWidth : fullLabelWidth;
    const canUseSide = sideSpace >= neededWidth + labelGap;
    let centreLabelX = useRight
      ? geometry.artworkRightPx + labelGap + (neededWidth / 2)
      : geometry.artworkLeftPx - labelGap - (neededWidth / 2);
    let centreLabelY = geometry.artworkCentreYPx + 5;
    let leaderStartX = useRight
      ? geometry.artworkCentreXPx + 10
      : geometry.artworkCentreXPx - 10;
    let leaderEndX = useRight
      ? centreLabelX - (neededWidth / 2) + 8
      : centreLabelX + (neededWidth / 2) - 8;
    let showLeader = true;

    if (!canUseSide) {
      centreLabelX = geometry.artworkCentreXPx;
      centreLabelY = geometry.artworkBottomPx + 22;
      showLeader = false;

      if (
        geometry.anchor &&
        geometry.anchor.type !== "none" &&
        geometry.yFromFloor(geometry.anchor.topHeightCm) - centreLabelY < 18
      ) {
        centreLabelY = geometry.artworkTopPx - 12;
      }
    }

    centreLabelX = clamp(centreLabelX, roomLeft + (neededWidth / 2), roomRight - (neededWidth / 2));
    centreLabelY = clamp(centreLabelY, geometry.wallTop + 36, geometry.floorY - 24);

    return [
      "<line x1='" + geometry.artworkCentreXPx.toFixed(1) + "' y1='" + (geometry.artworkCentreYPx - crosshairHalfHeight).toFixed(1) + "' x2='" + geometry.artworkCentreXPx.toFixed(1) + "' y2='" + (geometry.artworkCentreYPx + crosshairHalfHeight).toFixed(1) + "' stroke='" + COLOURS.centreLine + "' stroke-width='0.6' stroke-dasharray='2 4' opacity='0.75'/>",
      "<circle cx='" + geometry.artworkCentreXPx.toFixed(1) + "' cy='" + geometry.artworkCentreYPx.toFixed(1) + "' r='" + centreDotRadius + "' fill='" + COLOURS.artwork + "'/>",
      showLeader ? "<line x1='" + leaderStartX.toFixed(1) + "' y1='" + geometry.artworkCentreYPx.toFixed(1) + "' x2='" + leaderEndX.toFixed(1) + "' y2='" + geometry.artworkCentreYPx.toFixed(1) + "' stroke='" + COLOURS.centreLine + "' stroke-width='0.65' opacity='0.8'/>" : "",
      centreInstructionLabel(centreLabelX, centreLabelY, geometry.artwork.centreFromFloorCm, { compact: compact || !canUseSide })
    ].join("");
  }

  App.renderRoomPreview = function (engineResult) {
    if (!engineResult || engineResult.status !== "ok" || !engineResult.previewGeometry) {
      setPreviewHeading(null);
      App.dom.previewNote.textContent = "—";
      App.dom.svgPreviewStage.innerHTML = "";
      return;
    }

    setPreviewHeading(engineResult);

    if (engineResult.previewFallbackMessage) {
      App.dom.previewNote.textContent = "";
      App.dom.svgPreviewStage.style.aspectRatio = "auto";
      App.dom.svgPreviewStage.innerHTML =
        "<div class=\"preview-fallback-card\">" +
        esc(engineResult.previewFallbackMessage) +
        "</div>";
      return;
    }

    const geometry = defineGeometry(engineResult);
    const recommendation = engineResult.recommendation;
    const viewBox = [
      geometry.viewBoxLeft.toFixed(1),
      geometry.viewBoxTop.toFixed(1),
      geometry.viewBoxWidth.toFixed(1),
      geometry.viewBoxHeight.toFixed(1)
    ].join(" ");
    App.dom.svgPreviewStage.style.aspectRatio = geometry.viewBoxWidth.toFixed(1) + " / " + geometry.viewBoxHeight.toFixed(1);
    const svgTitleId = "roomPreviewSvgTitle";
    const svgDescId = "roomPreviewSvgDesc";
    const svg = [
      "<svg xmlns='http://www.w3.org/2000/svg' viewBox='" + viewBox + "' role='img' aria-labelledby='" + svgTitleId + " " + svgDescId + "'>",
      "<title id='" + svgTitleId + "'>" + esc(previewTitle(engineResult)) + "</title>",
      "<desc id='" + svgDescId + "'>" + esc(previewDescription(engineResult, geometry)) + "</desc>",
      drawStructure(geometry),
      drawArtwork(geometry),
      drawFurniture(geometry),
      drawMeasurements(geometry),
      drawLabels(geometry),
      "</svg>"
    ].join("");

    App.dom.previewNote.textContent = geometry.artwork.pieceCount + " × " + recommendation.isoSize + " " + orientationLabel(recommendation.orientation) + " · " + layoutLabel(recommendation.layout) + " · " + App.formatValue(geometry.artwork.totalArtworkWidthCm) + " " + App.unitLabel() + " total width";
    App.dom.svgPreviewStage.innerHTML = svg;
  };
}());
